import React from 'react'
import { XStack, YStack } from 'tamagui'
import { Trans } from '@lingui/react/macro'
import { useLingui } from '@lingui/react/macro'

import { Card } from '@newComponents/Card'
import { Icon } from '@newComponents/Icon'
import { Paragraph } from '@newComponents/Text'
import { Link } from '@newComponents/Link'

import { DocSection } from '../shared/DocSection'
import { DocScreenshot } from '../shared/DocScreenshot'
import { DocStep } from '../shared/DocStep'

const Forms = () => {
  const { t } = useLingui()

  return (
    <>
      <Card gap='$4'>
        <Paragraph size='$5'>
          <Trans>
            Membership Forms let Organizers collect information from people who want
            to join their Guild. When a form is enabled, clicking the Join button on
            the Guild's profile page opens the form instead of adding the person
            immediately. This gives Organizers the ability to learn about
            prospective Members before granting access.
          </Trans>
        </Paragraph>
        <Paragraph size='$5'>
          <Trans>
            Forms are optional. By default, Guilds are open and anyone can join
            with a single click. Enable a form when you want to collect information
            upfront or control who can join.
          </Trans>
        </Paragraph>
        <DocScreenshot
          src='docs/guilds/guild-settings-members.png'
          alt={t`Guild Settings Members page showing membership form configuration with form fields and approval options`}
          caption={t`Membership Forms are configured in the Members section of Guild Settings`}
        />
      </Card>

      <Card gap='$4'>
        <DocSection id='setting-up-a-form' title={t`Setting Up a Form`}>
          <Paragraph size='$5'>
            <Trans>
              Only <Link href='/docs/guilds/members'>Organizers</Link> can create and
              manage Membership Forms.
            </Trans>
          </Paragraph>
          <DocStep
            number={1}
            title={t`Open Guild Settings`}
            description={t`Navigate to your Guild's profile page and click the settings gear icon.`}
          />
          <DocStep
            number={2}
            title={t`Go to the Members section`}
            description={t`In Settings, navigate to the Members section where you will find membership form options.`}
          />
          <DocStep
            number={3}
            title={t`Enable the Membership Form`}
            description={t`Toggle on the Membership Form option. Once enabled, visitors who click Join will be presented with the form.`}
          />
          <DocStep
            number={4}
            title={t`Add form fields`}
            description={t`Add the fields you want prospective Members to fill out. Choose from field types including text, email, URL, multi-select, checkbox, and date. Each field can be marked as required or optional.`}
          />
          <DocStep
            number={5}
            title={t`Save your form`}
            description={t`Click Save to activate the form. New join requests will now go through this form.`}
          />
        </DocSection>
      </Card>

      <Card gap='$4'>
        <DocSection id='form-field-types' title={t`Form Field Types`}>
          <Paragraph size='$5'>
            <Trans>
              Membership Forms support a variety of field types so you can collect
              exactly the information you need:
            </Trans>
          </Paragraph>
          <YStack gap='$3'>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='edit' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>Text</Paragraph> — A single-line
                  or multi-line text input. Use this for open-ended questions like "What
                  brings you to our community?" or "What is your area of expertise?"
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='mail' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>Email</Paragraph> — An email
                  address input with validation.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='link' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>URL</Paragraph> — A link input,
                  useful for collecting portfolio URLs, LinkedIn profiles, or GitHub
                  accounts.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='checkmark' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>Multi-select</Paragraph> — A set
                  of predefined options where the applicant can select one or more. Great
                  for questions like "Which topics are you interested in?" with options
                  like React, TypeScript, Node.js, etc.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='checkmark' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>Checkbox</Paragraph> — A single
                  yes/no option. Useful for agreements like "I agree to the community
                  code of conduct."
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='calendar' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>Date</Paragraph> — A date picker,
                  useful for questions that need a specific date.
                </Trans>
              </Paragraph>
            </XStack>
          </YStack>
        </DocSection>
      </Card>

      <Card gap='$4'>
        <DocSection id='approval-workflow' title={t`Approval Workflow`}>
          <Paragraph size='$5'>
            <Trans>
              When a Membership Form has{' '}
              <Paragraph size='$5' fontWeight='$7'>approval required</Paragraph>{' '}
              enabled, submitted applications are held in a pending state until an
              Organizer reviews them. Organizers see a list of pending applications
              and can approve or deny each one individually.
            </Trans>
          </Paragraph>
          <Paragraph size='$5'>
            <Trans>
              When an application is approved, the person becomes a Member with the
              default Member role. Organizers can later promote them to Editor or
              Organizer through the{' '}
              <Link href='/docs/guilds/members'>Members</Link> page.
            </Trans>
          </Paragraph>
          <Paragraph size='$5'>
            <Trans>
              If approval is not required, the form still collects information but
              the person is added to the Guild immediately after submitting the form.
              The form responses are still available to Organizers in the Members
              section and in member data exports.
            </Trans>
          </Paragraph>
        </DocSection>
      </Card>

      <Card gap='$4'>
        <DocSection id='viewing-form-responses' title={t`Viewing Form Responses`}>
          <Paragraph size='$5'>
            <Trans>
              All form responses are stored and available to Organizers. You can view
              individual responses from the Members section in Guild Settings, or
              export the full member list as a CSV that includes form response data.
              See{' '}
              <Link href='/docs/guilds/members'>Managing Members</Link> for details
              on exporting member data.
            </Trans>
          </Paragraph>
        </DocSection>
      </Card>
    </>
  )
}

export default Forms
